import { Box, ButtonBase, useTheme } from "@mui/material";
import Subheading from "../../../../theme/text/Subheading";
import Text from "../../../../theme/text/Text";
import { booleanValueTypes } from "../../../../Streams/Conditions/ConditionSettings";
import { getOrderStateData } from "../../../../../utils/labelColorText";
import ShLabel from "../../../../theme/label/ShLabel";
import { useTranslation } from "react-i18next";
import { themeSettings, tokens } from "../../../../../theme";
import { ReactComponent as VirtualProduct } from '../../../../../styles/svg/virtuaproduct.svg';
import { ENV_TYPES } from "../../../Sidebar";
import { useSelector } from "react-redux";
import { general_states } from "../../../../../utils/staticEnums";
import { formatPrice } from '../../../../../utils/helpers';
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import SHQuickAction from "../../../../theme/Modal/SHQuickAction";
import { ProductHoverDetails } from "../../../../../environments/warehouse/echeck2/orders/OrdersEcheckView2";
import { useLocation } from "react-router-dom";
import { buildSidepanelUrl, makeSidepanelClickHandler } from "../../popout/sidepanelLink";
import { SIDEPANEL_KEYS } from "../../popout/sidepanelRegistry";

const LinePriceBox = ({ line, sx }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const priceExVat = Number(line?.sellingPriceExVAT) || 0;
    const price = Number(line?.sellingPrice) || 0;
    if(priceExVat === 0 && ((price - priceExVat) === 0 )) {
        return null;
    }
    return (
        <Box
            sx={{
                p: 1,
                py: 1,
                borderRadius: 3,
                background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                display: "flex",
                flexDirection: "column",
                ...sx,
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text light>{t("total_price_ex_vat")}</Text>
                <Text>{formatPrice(t, priceExVat)}</Text>
            </Box>
            {(price - priceExVat) > 0 &&
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Text light>{t("total_vat")}</Text>
                    <Text>{formatPrice(t, price - priceExVat)}</Text>
                </Box>
            }
            <Box
                sx={{
                    mt: 1,
                    pt: 1,
                    borderTop: `1px solid ${theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[200]}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Text>{t("total_price")}</Text>
                <Text bold>{formatPrice(t, price)}</Text>
            </Box>
        </Box>
    );
};

// Hover content for a best-before label: every batch/lot behind that date.
const BbDateHoverDetails = ({ entries }) => {
    const { t, i18n } = useTranslation();

    return (
        <Box
            sx={{
                p:1.5,
                display:"flex",
                flexDirection:"column",
                gap:0.5,
            }}
        >
            <Text light>{t("bb_date")}</Text>
            {entries.map((entry,index) => (
                <Text key={"bbdatedetail"+index} semibold>
                    {`${t(entry.type)} - ${entry.number} - ${new Date(entry.bbDate).toLocaleDateString(i18n.language)}`}
                </Text>
            ))}
        </Box>
    );
};

// A line can be picked from several batches/lots, so bbDates holds one entry per
// source — show each distinct best-before date once and reveal its batches/lots on hover.
const BbDateLabels = ({ line }) => {
    const { t, i18n } = useTranslation();

    const entriesByDate = new Map();
    (line?.bbDates ?? []).forEach(entry => {
        const date = new Date(entry?.bbDate);
        if(isNaN(date.getTime())) {
            return;
        }
        const formattedDate = date.toLocaleDateString(i18n.language);
        if(!entriesByDate.has(formattedDate)) {
            entriesByDate.set(formattedDate, []);
        }
        entriesByDate.get(formattedDate).push(entry);
    });

    if(entriesByDate.size === 0) {
        return null;
    }

    // Several batches stack underneath each other so the line keeps its width.
    return (
        <Box
            sx={{
                display:"flex",
                flexDirection:"column",
                alignItems:"flex-start",
                gap:0.5,
            }}
        >
        {[...entriesByDate].map(([date,entries],index) => (
        <SHQuickAction
            key={line.id+"bbdate"+index}
            component={<BbDateHoverDetails entries={entries}/>}
            anchorOrigin={{
                vertical:'center',
                horizontal:"right"
            }}
            transformOrigin={{
                vertical:'center',
                horizontal:"left"
            }}
            sx={{
                modal:{
                    body:{
                        ml:2,
                        "@media screen and (max-width: 47.9375em)" : {
                            ml:0,
                        }
                    }
                }
            }}
            disableAutoFocus
            disableEnforceFocus
        >
            {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                <Box
                    aria-owns={anchorEl ? 'simple-popover' : undefined}
                    aria-haspopup="true"
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onTouchStart={onTouchStart}
                >
                    <ShLabel
                        fitted
                        size={32}
                        palette="normal"
                        variant="orange"
                        sx={{
                            whiteSpace:"nowrap",
                        }}
                    >
                        {`${t("bb_date")}: ${date}`}
                    </ShLabel>
                </Box>
            )}
        </SHQuickAction>
        ))}
        </Box>
    );
};

export const OrderlinesComp = (props) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { orderLinesData, isLoadingLines } = props;

    const toBoolean = value => {
        if (value === true || value === false) return value;      // already boolean
        if (value === "true") return true;
        if (value === "false") return false;
        return Boolean(value); // fallback for other cases
    };

    const hasPricingPermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.OUTBOUND_ORDER_VALUE,UserPrivileges.RP_INVOICE_READ])
    );
    
    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];

    const { pathname } = useLocation();

    // Once the order has shipped, a relation shouldn't still see lines as picked or
    // fullypicked — those are warehouse-internal steps, the line left the building.
    const getLineStateData = (state) => {
        if(isRelation
            && props.orderState === general_states.SHIPPED
            && [general_states.PICKED, general_states.FULLYPICKED].includes(state)) {
            return {
                labelcolor: "green",
                labeltext: "shipped",
                state: general_states.SHIPPED,
            };
        }
        return getOrderStateData(state, theme, isRelation);
    };

    return (
        <Box
            sx={{
                p:3,
            }}
        >
            {!props.hideTitle && (
                <Subheading
                    sx={{
                        mb:1.5,
                        "@media screen and (max-width: 47.9375em)" : {
                            display:"none",
                        }
                    }}
                >
                    {t("orderlines")}
                </Subheading>
            )}
            <Box
                sx={{
                    display:"flex",
                    gap:2,
                    flexDirection:'column',
                }}
            >
                {!isLoadingLines && orderLinesData &&
                    orderLinesData.data.map((line,olidex) => (
                        line.children.length > 0 ?
                        <Box
                            key={"prod"+olidex}
                            sx={{
                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                borderRadius:3,

                            }}
                        >
                            {/* Header row */}
                            <ButtonBase
                                component="a"
                                href={buildSidepanelUrl(pathname, SIDEPANEL_KEYS.VPRODUCT, line.product.id)}
                                onClick={makeSidepanelClickHandler(() => {
                                    if(props.getVirtualProductDetails){
                                        props.getVirtualProductDetails(line);
                                    }
                                })}
                                sx={{
                                    width:"calc(100% - 16px)",
                                    m:1,
                                    p:1.5,
                                    borderRadius:2,
                                    display:"flex",
                                    justifyContent:"space-between",
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:'column',
                                        alignItems:"flex-start",
                                        justifyContent:"flex-start",
                                        gap:1.5,
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        display:"flex",
                                        gap:1.5,
                                        mr:2,
                                    }}
                                >
                                    {/* Amount */}
                                    <Box
                                        sx={{
                                            height:32,
                                            width:32,
                                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                            border:`1px solid ${theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400]}`,
                                            color:colors.txt["primary"],
                                            borderRadius:4,
                                            display:"flex",
                                            alignItems:'center',
                                            justifyContent:"center",
                                            flexShrink:0,
                                        }}
                                    >
                                        <Text bold>{line.amount}</Text>
                                    </Box>

                                    {/* Sku description serialnumbers list and productfields */}
                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:"column",
                                        }}
                                    >
                                        <Text variant={theme.palette.mode === "dark" ? "blue" : "primary"}>
                                            {line.product.sku}
                                        </Text>
                                        <Text>
                                            {line.product.description}
                                        </Text>

                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        background:theme.palette.mode === "dark" ? colors.orange[100] : "#FEF0E5",
                                        color: theme.palette.mode === "dark" ? colors.orange[400] : "#F56900",
                                        border:`1px solid ${theme.palette.mode === "dark" ? colors.orange[400] : "#F56900"}`,
                                        height:32,
                                        lineHeight:"32px",
                                        display:'flex',
                                        alignItems:'center',
                                        whiteSpace:"nowrap",
                                        gap:1,
                                        px:2,
                                        borderRadius:4,
                                        fontWeight:700,
                                        fontSize:14,
                                        fontFamily:"Roboto,sans-serif",
                                        "@media screen and (max-width: 47.9375em)" : {
                                            width:1,
                                        },
                                        "& .virtual_svg *" : {
                                            fill: theme.palette.mode === "dark" ? colors.orange[400] : "#F56900"
                                        }
                                    }}
                                >
                                    <VirtualProduct
                                        className="virtual_svg"
                                    />
                                    {t("virtual_product")}
                                </Box>
                            </ButtonBase>

                            {/* Children */}
                            <Box
                                sx={{
                                    mx:1,
                                    pb:1,
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:1,
                                }}
                            >
                                {line.children.map((child,cidex) => {
                                    let labelcolor;
                                    let labeltext;
                                    if(child.type === "add_on") {
                                        labeltext=t("add_on");
                                        labelcolor="orange";
                                    }else{
                                        const stateData = getLineStateData(child.state);
                                        labelcolor = stateData.labelcolor;
                                        labeltext = stateData.labeltext;
                                    }
                                    
                                    return (
                                        <ButtonBase
                                            component="a"
                                            href={buildSidepanelUrl(pathname, SIDEPANEL_KEYS.PRODUCT, child.product.id)}
                                            onClick={makeSidepanelClickHandler(() => props.getProductDetails(child))}
                                            key={"lc"+olidex+cidex}
                                            sx={{
                                                width:1,
                                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                borderRadius:2,
                                                px:1.5,
                                                py:1.5,
                                                display:"flex",
                                                justifyContent:"flex-start",
                                                flexDirection:'column',
                                                alignItems:'flex-start',
                                            }}
                                        >

                                            <Box
                                                sx={{
                                                    display:"flex",
                                                    justifyContent:"space-between",
                                                    gap:1.5,
                                                    width:1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display:"flex",
                                                        alignItems:'flex-start',
                                                        gap:1.5,
                                                        mr:2,
                                                    }}
                                                >
                                                    
                                                    <Box
                                                        sx={{
                                                            height:32,
                                                            width:32,
                                                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                            border:`1px solid ${theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400]}`,
                                                            color:colors.txt["primary"],
                                                            borderRadius:4,
                                                            display:"flex",
                                                            alignItems:'center',
                                                            justifyContent:"center",
                                                            flexShrink:0,
                                                        }}
                                                    >
                                                        <Text bold>{child.amount}</Text>
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            display:"flex",
                                                            flexDirection:"column",
                                                        }}
                                                    >
                                                        <Text semibold>
                                                            {child.product.sku}
                                                        </Text>
                                                        <Text light>
                                                            {child.product.description}
                                                        </Text>
                                                    </Box>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display:'flex',
                                                        gap:1.5,
                                                    }}
                                                >
                                                    <BbDateLabels line={child}/>
                                                    {(!isRelation && child.warehouseLocations?.length > 0 && child.warehouseLocations?.length < 3) ? child.warehouseLocations.map((item,index) => (
                                                        <SHQuickAction
                                                            key={child.id+"warehouselocation"+index}
                                                            component={<ProductHoverDetails productId={child.product?.id} product={child.product}/>}
                                                            anchorOrigin={{
                                                                vertical:'center',
                                                                horizontal:"right"
                                                            }}
                                                            transformOrigin={{
                                                                vertical:'center',
                                                                horizontal:"left"
                                                            }}
                                                            sx={{
                                                                modal:{
                                                                    body:{
                                                                        ml:2,
                                                                        "@media screen and (max-width: 47.9375em)" : {
                                                                            ml:0,
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                            disableAutoFocus
                                                            disableEnforceFocus
                                                        >
                                                            {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                                                                <Box
                                                                    aria-owns={anchorEl ? 'simple-popover' : undefined}
                                                                    aria-haspopup="true"
                                                                    onMouseEnter={onMouseEnter}
                                                                    onMouseLeave={onMouseLeave}
                                                                    onTouchStart={onTouchStart}
                                                                    sx={{
                                                                        height:32,
                                                                        lineHeight:"32px",
                                                                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                                                        color:colors.txt['primary'],
                                                                        borderRadius:4,
                                                                        px:2,
                                                                        fontSize:14,
                                                                        fontWeight:500,
                                                                        fontFamily:"Roboto, sans-serif",
                                                                        whiteSpace:"nowrap",
                                                                        transition:"200ms",
                                                                        "&:hover" : {
                                                                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                                            transition:"200ms",
                                                                        }
                                                                    }}
                                                                >
                                                                    {item}
                                                                </Box>
                                                            )}
                                                        </SHQuickAction>
                                                    )) : (!isRelation && child.warehouseLocations?.length > 0 && child.warehouseLocations?.length >= 3)  && (

                                                        <SHQuickAction
                                                            component={<ProductHoverDetails productId={child.product?.id} product={child.product}/>}
                                                            anchorOrigin={{
                                                                vertical:'center',
                                                                horizontal:"right"
                                                            }}
                                                            transformOrigin={{
                                                                vertical:'center',
                                                                horizontal:"left"
                                                            }}
                                                            sx={{
                                                                modal:{
                                                                    body:{
                                                                        ml:2,
                                                                        "@media screen and (max-width: 47.9375em)" : {
                                                                            ml:0,
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                            disableAutoFocus
                                                            disableEnforceFocus
                                                        >
                                                            {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                                                                <Box
                                                                    aria-owns={anchorEl ? 'simple-popover' : undefined}
                                                                    aria-haspopup="true"
                                                                    onMouseEnter={onMouseEnter}
                                                                    onMouseLeave={onMouseLeave}
                                                                    onTouchStart={onTouchStart}
                                                                    sx={{
                                                                        height:32,
                                                                        lineHeight:"32px",
                                                                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                                                        display:"flex",
                                                                        gap:1,
                                                                        borderRadius:4,
                                                                        px:2,
                                                                        transition:"200ms",
                                                                        "&:hover" : {
                                                                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                                            transition:"200ms",
                                                                        }
                                                                    }}
                                                                >
                                                                    <Text bold>{child.warehouseLocations.length}</Text> <Text light semibold>{t('locations')}</Text>
                                                                </Box>
                                                            )}
                                                        </SHQuickAction>
                                                    )}
                                                    <ShLabel fitted size={32} palette="normal" variant={labelcolor}
                                                        sx={child.state === general_states.BACKORDERED &&{
                                                            display:"flex",
                                                            alignItems:'center',
                                                            gap:1.5,
                                                            pr:0.5,
                                                        }}
                                                    >
                                                        {t(labeltext)}
                                                        {child.state === general_states.BACKORDERED && (
                                                            <Box
                                                                sx={{
                                                                    background:colors.pink[400],
                                                                    color:theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light'],
                                                                    height:24,
                                                                    borderRadius:4,
                                                                    px:1.5,
                                                                    lineHeight:"24px",
                                                                    fontWeight:"700",
                                                                    fontFamily:"Roboto, sans-serif",
                                                                    fontSize:14,
                                                                }}
                                                            >
                                                                {child.backorder}
                                                            </Box>
                                                        )}
                                                    </ShLabel>

                                                    {(child.state === general_states.BACKORDERED && child.expectedDeliveryDate) && (
                                                        <Box
                                                            sx={{
                                                                mt:0.5,
                                                            }}
                                                        >
                                                            <Text light>
                                                                {t("expected")} 
                                                            </Text>

                                                            <Text semibold sx={{ml:0.5,}}>
                                                                {child.expectedDeliveryDate &&
                                                                    new Date(child.expectedDeliveryDate)
                                                                    .toLocaleDateString(i18n.language)} 
                                                            </Text>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>


                                            {child.product?.productFieldValues?.some(
                                                pfv => pfv.value !== null && pfv.value !== "" && pfv.value !== undefined &&
                                                    (!booleanValueTypes.includes(pfv.productfield.valueType) || toBoolean(pfv.value))
                                            ) && (
                                                <Box
                                                    sx={{
                                                        width:`calc(100% - 40px)`,
                                                        mt:1,
                                                        ml:5,
                                                        display:"flex",
                                                        flexDirection:"column",
                                                        gap:0.5,
                                                    }}
                                                >
                                                    {child.product.productFieldValues
                                                        .filter(pfv =>
                                                            pfv.value !== null &&
                                                            pfv.value !== "" &&
                                                            pfv.value !== undefined &&
                                                            (!booleanValueTypes.includes(pfv.productfield.valueType) || toBoolean(pfv.value))
                                                        )
                                                        .map((pfv, pfvidex) => {
                                                            
                                                            return (
                                                                booleanValueTypes.includes(pfv.productfield.valueType) ?
                                                                <Box
                                                                    key={"pfv" + olidex + pfvidex}
                                                                    sx={{
                                                                        height:24,
                                                                        border:`1px solid ${toBoolean(pfv.value) ? colors.green[400] : colors.red[400]}`,
                                                                        background: toBoolean(pfv.value) ? colors.green[100] : colors.red[100],
                                                                        borderRadius:3,
                                                                        display:'flex',
                                                                        alignItems:"center",
                                                                        justifyContent:"space-between",
                                                                        px:1,
                                                                        "@media screen and (max-width: 47.9375em)" : {
                                                                            flexWrap:"wrap",
                                                                            height:"unset",
                                                                            py:0.5,
                                                                        }
                                                                    }}
                                                                >
                                                                    <Text>
                                                                        {pfv.productfield?.name}
                                                                    </Text>

                                                                    <Box
                                                                        sx={{
                                                                            color: toBoolean(pfv.value) ? colors.green[400] : colors.red[400],
                                                                            fontSize:14,
                                                                            fontWeight:700,
                                                                            fontFamily:"Roboto,sans-serif",
                                                                            lineHeight:"16px",
                                                                        }}
                                                                    >
                                                                        {t(toBoolean(pfv.value) ? "yes" : 'no')}
                                                                    </Box>
                                                                </Box>
                                                                :
                                                                <Box
                                                                    key={"pfv" + olidex + pfvidex}
                                                                    sx={{
                                                                        height:24,
                                                                        border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[200]}`,
                                                                        background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                                                                        borderRadius:3,
                                                                        display:'flex',
                                                                        alignItems:"center",
                                                                        justifyContent:"space-between",
                                                                        px:1,
                                                                        "@media screen and (max-width: 47.9375em)" : {
                                                                            flexWrap:"wrap",
                                                                            height:"unset",
                                                                            py:0.5,
                                                                        }
                                                                    }}
                                                                >
                                                                    <Text light>
                                                                        {pfv.productfield?.name}
                                                                    </Text>

                                                                    <Text>
                                                                        {pfv.value.toString()}
                                                                    </Text>
                                                                </Box>

                                                            )
                                                        })
                                                    }
                                                </Box>
                                            )}


                                            {/* Serialnumbers */}
                                            {child.serialnumbers?.length > 0 &&
                                                <Box
                                                    sx={{
                                                        mt:1,
                                                        display:"flex",
                                                        justifyContent:'flex-start',
                                                        gap:1,
                                                        flexWrap:"wrap",
                                                        ml:5,
                                                    }}
                                                >
                                                    {line.serialnumbers.map((sn,snidex) => (
                                                        <Box 
                                                            key={"lsn"+olidex+snidex}
                                                            sx={{
                                                                height:24,
                                                                px:1,
                                                                lineHeight:"24px",
                                                                borderRadius:1.5,
                                                                background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                                                                color:theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"],
                                                                fontSize:14,
                                                                fontWeight:500,
                                                                fontFamily:"Roboto, sans-serif",
                                                            }}
                                                        >
                                                            {sn.barcode}
                                                        </Box>
                                                    ))} 
                                                </Box>
                                            }
                                        </ButtonBase>
                                    )
                                })}
                            </Box>
                            {props.hasReadPricePermission &&
                                <LinePriceBox
                                    line={line}
                                    sx={{  
                                        mb: 1, 
                                        mx: 1,
                                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[50],
                                        px:2,
                                        py:1.5,
                                    }}
                                />
                            }
                        </Box>
                        :
                        (() => {
                            let labelcolor;
                            let labeltext;

                            if(line.type === "add_on") {
                                labeltext=t("add_on");
                                labelcolor="orange";
                            }else{
                                const stateData = getLineStateData(line.state);
                                labelcolor = stateData.labelcolor;
                                labeltext = stateData.labeltext;
                            }


                            return (
                                
                                <ButtonBase
                                    key={"prod"+olidex}
                                    component="a"
                                    href={buildSidepanelUrl(pathname, SIDEPANEL_KEYS.PRODUCT, line.product.id)}
                                    onClick={makeSidepanelClickHandler(() => props.getProductDetails(line))}
                                    sx={{
                                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                        borderRadius:3,
                                        p:2.5,
                                        width:1,
                                        display:'flex',
                                        flexDirection:"column",
                                        alignItems:'flex-start',
                                    }}
                                >
                                        <Box
                                        sx={{
                                            display:"flex",
                                            justifyContent:"space-between",
                                            gap:1.5,
                                            width:1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display:"flex",
                                                alignItems:'flex-start',
                                                gap:1.5,
                                                mr:2,
                                            }}
                                        >
                                            
                                            <Box
                                                sx={{
                                                    height:32,
                                                    width:32,
                                                    background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                    border:`1px solid ${theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400]}`,
                                                    color:colors.txt["primary"],
                                                    borderRadius:4,
                                                    display:"flex",
                                                    alignItems:'center',
                                                    justifyContent:"center",
                                                    flexShrink:0,
                                                }}
                                            >
                                                <Text bold>{line.amount}</Text>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display:"flex",
                                                    flexDirection:"column",
                                                }}
                                            >
                                                <Text semibold>
                                                    {line.product.sku}
                                                </Text>
                                                <Text light>
                                                    {line.product.description}
                                                </Text>
                                                
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                display:'flex',
                                                gap:1.5,
                                            }}
                                        >
                                            <BbDateLabels line={line}/>

                                            {(!isRelation && line.warehouseLocations?.length > 0 && line.warehouseLocations?.length < 3) ? line.warehouseLocations.map((item,index) => (
                                                <SHQuickAction
                                                    key={line.id+"warehouselocation"+index}
                                                    component={<ProductHoverDetails productId={line.product?.id} product={line.product}/>}
                                                    anchorOrigin={{
                                                        vertical:'center',
                                                        horizontal:"right"
                                                    }}
                                                    transformOrigin={{
                                                        vertical:'center',
                                                        horizontal:"left"
                                                    }}
                                                    sx={{
                                                        modal:{
                                                            body:{
                                                                ml:2,
                                                                "@media screen and (max-width: 47.9375em)" : {
                                                                    ml:0,
                                                                }
                                                            }
                                                        }
                                                    }}
                                                    disableAutoFocus
                                                    disableEnforceFocus
                                                >
                                                    {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                                                        <Box
                                                            aria-owns={anchorEl ? 'simple-popover' : undefined}
                                                            aria-haspopup="true"
                                                            onMouseEnter={onMouseEnter}
                                                            onMouseLeave={onMouseLeave}
                                                            onTouchStart={onTouchStart}
                                                            sx={{
                                                                height:32,
                                                                lineHeight:"32px",
                                                                background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                                                                color:colors.txt['primary'],
                                                                borderRadius:4,
                                                                px:2,
                                                                whiteSpace:"nowrap",
                                                                fontSize:14,
                                                                fontWeight:500,
                                                                fontFamily:"Roboto, sans-serif",
                                                                transition:"200ms",
                                                                "&:hover" : {
                                                                    background:theme.palette.mode === "dark" ? colors.grey[700] : colors.grey[200],
                                                                    transition:"200ms",
                                                                }
                                                            }}
                                                        >
                                                            {item}
                                                        </Box>
                                                    )}
                                                </SHQuickAction>
                                            )) : (!isRelation && line.warehouseLocations?.length > 0 && line.warehouseLocations?.length >= 3)  && (

                                                <SHQuickAction
                                                    component={<ProductHoverDetails productId={line.product?.id} product={line.product}/>}
                                                    anchorOrigin={{
                                                        vertical:'center',
                                                        horizontal:"right"
                                                    }}
                                                    transformOrigin={{
                                                        vertical:'center',
                                                        horizontal:"left"
                                                    }}
                                                    sx={{
                                                        modal:{
                                                            body:{
                                                                ml:2,
                                                                "@media screen and (max-width: 47.9375em)" : {
                                                                    ml:0,
                                                                }
                                                            }
                                                        }
                                                    }}
                                                    disableAutoFocus
                                                    disableEnforceFocus
                                                >
                                                    {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                                                        <Box
                                                            aria-owns={anchorEl ? 'simple-popover' : undefined}
                                                            aria-haspopup="true"
                                                            onMouseEnter={onMouseEnter}
                                                            onMouseLeave={onMouseLeave}
                                                            onTouchStart={onTouchStart}
                                                            sx={{
                                                                height:32,
                                                                lineHeight:"32px",
                                                                background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                                                                display:"flex",
                                                                gap:1,
                                                                borderRadius:4,
                                                                px:2,
                                                                transition:"200ms",
                                                                "&:hover" : {
                                                                    background:theme.palette.mode === "dark" ? colors.grey[700] : colors.grey[200],
                                                                    transition:"200ms",
                                                                }
                                                            }}
                                                        >
                                                            <Text bold>{line.warehouseLocations.length}</Text> <Text light semibold>{t('locations')}</Text>
                                                        </Box>
                                                    )}
                                                </SHQuickAction>
                                            )}
                                            
                                            <ShLabel fitted size={32} palette="normal" variant={labelcolor}
                                                sx={line.state === general_states.BACKORDERED &&{
                                                    display:"flex",
                                                    alignItems:'center',
                                                    gap:1.5,
                                                    pr:0.5,
                                                }}
                                            >
                                                {t(labeltext)}
                                                {line.state === general_states.BACKORDERED && (
                                                    <Box
                                                        sx={{
                                                            background:colors.pink[400],
                                                            color:theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light'],
                                                            height:24,
                                                            borderRadius:4,
                                                            px:1.5,
                                                            lineHeight:"24px",
                                                            fontWeight:"700",
                                                            fontFamily:"Roboto, sans-serif",
                                                            fontSize:14,
                                                        }}
                                                    >
                                                        {line.backorder}
                                                    </Box>
                                                )}
                                            </ShLabel>
                                            {(line.state === general_states.BACKORDERED && line.expectedDeliveryDate) && (
                                                <Box
                                                    sx={{
                                                        mt:0.5,
                                                    }}
                                                >
                                                    <Text light>
                                                        {t("expected")} 
                                                    </Text>

                                                    <Text semibold sx={{ml:0.5,}}>
                                                        {line.expectedDeliveryDate &&
                                                            new Date(line.expectedDeliveryDate)
                                                            .toLocaleDateString("en-GB")} 
                                                    </Text>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                    {line.product?.productFieldValues?.some(
                                        pfv => pfv.value !== null && pfv.value !== "" && pfv.value !== undefined &&
                                            (!booleanValueTypes.includes(pfv.productfield.valueType) || toBoolean(pfv.value))
                                    ) && (
                                        <Box
                                            sx={{
                                                width:`calc(100% - 40px)`,
                                                mt:1.5,
                                                ml:5,
                                                display:"flex",
                                                flexDirection:"column",
                                                gap:0.5,
                                            }}
                                        >
                                            {line.product.productFieldValues
                                                .filter(pfv => pfv.value !== null && pfv.value !== "" && pfv.value !== undefined && (!booleanValueTypes.includes(pfv.productfield.valueType) || toBoolean(pfv.value)))
                                                .map((pfv, pfvidex) => {
                                                    
                                                    
                                                    return (
                                                        booleanValueTypes.includes(pfv.productfield.valueType) ?
                                                        <Box
                                                            key={"pfv"+olidex+pfvidex}
                                                            sx={{
                                                                height:24,
                                                                border:`1px solid ${toBoolean(pfv.value) ? colors.green[400] : colors.red[400]}`,
                                                                background: toBoolean(pfv.value) ? colors.green[100] : colors.red[100],
                                                                borderRadius:3,
                                                                display:'flex',
                                                                alignItems:"center",
                                                                justifyContent:"space-between",
                                                                px:1,
                                                            }}
                                                        >
                                                            <Text>
                                                                {pfv.productfield?.name}
                                                            </Text>
                                                            <Box
                                                                sx={{
                                                                    color: toBoolean(pfv.value) ? colors.green[400] : colors.red[400],
                                                                    fontSize:14,
                                                                    fontWeight:700,
                                                                    fontFamily:"Roboto,sans-serif",
                                                                    lineHeight:"16px",
                                                                }}
                                                            >
                                                                {t(toBoolean(pfv.value) ? "yes" : 'no')}
                                                            </Box>
                                                        </Box>
                                                        :

                                                        <Box
                                                            key={"pfv"+olidex+pfvidex}
                                                            sx={{
                                                                height:24,
                                                                // border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[200]}`,
                                                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                                borderRadius:3,
                                                                display:'flex',
                                                                alignItems:"center",
                                                                justifyContent:"space-between",
                                                                px:1,
                                                            }}
                                                        >
                                                            <Text>
                                                                {pfv.productfield?.name}
                                                            </Text>

                                                            <Text semibold>{pfv.value?.toString()}</Text>
                                                        </Box>
                                                    )
                                                })
                                            }
                                        </Box>
                                    )}
                                    {line.serialnumbers?.length > 0 &&
                                        <Box
                                            sx={{
                                                mt:1.5,
                                                display:"flex",
                                                gap:1,
                                                flexWrap:"wrap",
                                                ml:5,
                                            }}
                                        >
                                            {line.serialnumbers.map((sn,snidex) => (
                                                <Box 
                                                    key={"lsn"+olidex+snidex}
                                                    sx={{
                                                        height:24,
                                                        px:1,
                                                        lineHeight:"24px",
                                                        borderRadius:1.5,
                                                        background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                                                        color:theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"],
                                                        fontSize:14,
                                                        fontWeight:500,
                                                        fontFamily:"Roboto, sans-serif",
                                                    }}
                                                >
                                                    {sn.barcode}
                                                </Box>
                                            ))}
                                        </Box>
                                    }
                                    {props.hasReadPricePermission &&
                                        <LinePriceBox
                                            line={line}
                                            sx={{ 
                                                mt: 1, 
                                                ml: 5, 
                                                width: "calc(100% - 40px)",
                                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[50]
                                            }}
                                        />
                                    }
                                </ButtonBase>
                            );
                        })()
                    ))
                }
            </Box>
        </Box>
    )
}